import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import type { TenantSettingsResponse } from '@/lib/api/controlPlaneClient'
import { SettingRow, Divider } from './SettingRow'

export function OrderSettings() {
  const {
    settings,
    isLoading,
    isError,
    updateAsync,
    isUpdating,
    queryKey,
  } = useStorefrontTenantSettings()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load order settings')
    }
  }, [isError])

  const handleToggleAutoSlipVerify = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, auto_slip_verify_enabled: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ auto_slip_verify_enabled: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleStaffManualVerification = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, allow_staff_manual_verification: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ allow_staff_manual_verification: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleAutoInvoice = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, auto_invoice_on_payment: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ auto_invoice_on_payment: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleEventRequireSlip = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, event_require_slip: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ event_require_slip: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  const handleToggleSkipSlipVerification = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old
          ? { ...old, skip_slip_verification: checked ? 1 : 0 }
          : old,
    )

    try {
      await updateAsync({ skip_slip_verification: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-0">
        {/* Auto Slip Verification */}
        <SettingRow label="Auto Slip Verification">
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.auto_slip_verify_enabled}
              onCheckedChange={handleToggleAutoSlipVerify}
              disabled={isUpdating}
              aria-label="Auto slip verification"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Event: Require Slip */}
        <SettingRow label="Require Slip for Event Orders">
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.event_require_slip}
              onCheckedChange={handleToggleEventRequireSlip}
              disabled={isUpdating}
              aria-label="Require payment slip for event orders"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Skip Slip Verification */}
        <SettingRow
          label="Skip Slip Verification"
          subtitle="Accept any uploaded image as proof of payment. You'll review and approve payments manually."
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.skip_slip_verification}
              onCheckedChange={handleToggleSkipSlipVerification}
              disabled={isUpdating}
              aria-label="Skip automatic payment slip verification"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Auto Invoice on Payment */}
        <SettingRow label="Auto-Create Invoice on Payment">
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.auto_invoice_on_payment}
              onCheckedChange={handleToggleAutoInvoice}
              disabled={isUpdating}
              aria-label="Auto-create invoice when payment is received"
            />
          )}
        </SettingRow>

        <Divider />

        {/* Staff Manual Slip Verification */}
        <SettingRow label="Staff Manual Slip Verification">
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.allow_staff_manual_verification}
              onCheckedChange={handleToggleStaffManualVerification}
              disabled={isUpdating}
              aria-label="Allow staff to manually upload and verify payment slips"
            />
          )}
        </SettingRow>
      </CardContent>
    </Card>
  )
}
